import { NavLink, useLocation } from 'react-router-dom'
import {
  Layers,
  LayoutDashboard,
  FolderOpen,
  GitBranch,
  Users,
  Settings,
  X,
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { workspaceApi } from '@/lib/api'
import { cn } from '@/lib/utils'
import { WorkspaceMenu } from './workspace-menu'

const MAIN_NAV = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/workspaces', label: 'Workspace', icon: Layers, end: false },
]

const WORKSPACE_NAV = [
  { label: 'Project', icon: FolderOpen, suffix: 'projects' },
  { label: 'Pipeline', icon: GitBranch, suffix: 'pipeline' },
  { label: 'Member', icon: Users, suffix: 'members' },
  { label: 'Pengaturan', icon: Settings, suffix: 'settings' },
]

interface SidebarProps {
  onClose?: () => void
}

export function Sidebar({ onClose }: SidebarProps) {
  const location = useLocation()

  // Extract workspace slug from current route (e.g. /workspaces/my-ws/tasks → "my-ws")
  const match = location.pathname.match(/^\/workspaces\/([^/]+)/)
  const currentSlug = match?.[1] ?? null

  const { data: workspace } = useQuery({
    queryKey: ['workspace', currentSlug],
    queryFn: () => workspaceApi.getWorkspace(currentSlug!),
    enabled: !!currentSlug,
  })

  const navItemClass = (isActive: boolean) =>
    cn(
      'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
      isActive
        ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium'
        : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
    )

  return (
    <aside className="flex h-full w-60 shrink-0 flex-col border-r border-sidebar-border bg-sidebar">
      {/* Header: WorkspaceMenu trigger */}
      <div className="flex h-14 items-center border-b border-sidebar-border px-2 gap-1">
        <WorkspaceMenu currentSlug={currentSlug} onNavigate={onClose} />
        {onClose && (
          <button
            onClick={onClose}
            aria-label="Tutup menu"
            className="rounded-md p-1 text-sidebar-foreground hover:bg-sidebar-accent transition-colors shrink-0"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-2 py-4 space-y-4">
        {/* Main nav */}
        <div className="space-y-0.5">
          {MAIN_NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={onClose}
              className={({ isActive }) => navItemClass(isActive)}
            >
              <item.icon className="h-4 w-4 shrink-0" />
              <span className="flex-1">{item.label}</span>
            </NavLink>
          ))}
        </div>

        {/* Workspace context sub-nav */}
        {currentSlug && (
          <div>
            <p className="mb-1 px-3 text-xs font-semibold uppercase tracking-wider text-sidebar-foreground/40 truncate">
              {workspace?.name ?? currentSlug}
            </p>
            <div className="space-y-0.5">
              {WORKSPACE_NAV.map((item) => (
                <NavLink
                  key={item.suffix}
                  to={`/workspaces/${currentSlug}/${item.suffix}`}
                  end={false}
                  onClick={onClose}
                  className={({ isActive }) => navItemClass(isActive)}
                >
                  <item.icon className="h-4 w-4 shrink-0" />
                  <span className="flex-1">{item.label}</span>
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </nav>

    </aside>
  )
}
